/**
 * @file event-bus.ts
 * @description Pub/Sub mechanism for decoupling HCIE components
 */

export interface HCIEEventMap {
    'TOOL_CANCEL_REQUESTED': { toolId?: string };
    'SELECTION_CHANGED': { active: boolean };
    'TOOL_CHANGED': { toolId: string };
    'LAYER_ADDED': { layerId: number };
    'DOC_CREATED': { index: number };
    'RENDER_LAYERS': void;
    'UPDATE_LAYER_PANEL': void;
    'RENDER_VECTOR_LAYER': { layer: any };
    'MOVE_LAYER': { from: number, to: number, skipRecord?: boolean };
    'APPLY_ZOOM': { zoom: number };
    'VECTOR_SHAPE_SELECTION_CHANGED': null;
    'DOCUMENT_SWITCHED': { index: number };
    'DOCUMENT_TABS_CHANGED': { tabs: any[] };
    'OPEN_NEW_IMAGE_DIALOG': null;
    'ADD_LAYER': { name?: string, type?: string };
    'SELECT_LAYER': { index: number };
    'RENDER_TEXT_LAYER': { layer: any };
    'FINISH_DRAWING': void;
    'UPDATE_PROPERTIES_PANEL': { type: string, value: any };
    'MOVE_SELECTION_MASK': { dx: number, dy: number };
    'PROJECT_LOADED': { layerCount: number, width?: number, height?: number };
    'PROJECT_RESIZE': { width: number, height: number };
    'PSD_LOADED': { width: number, height: number, layerCount: number };
    'HISTORY_CHANGED': { actions: any[], currentIndex: number } | void;
    [key: string]: any;
}

type EventHandler<K extends keyof HCIEEventMap> = (payload: HCIEEventMap[K]) => void;

class EventBusImpl {
    private listeners: Map<string, Array<EventHandler<any>>> = new Map();

    public on<K extends keyof HCIEEventMap>(event: K, handler: EventHandler<K>): void {
        const key = event as string;
        if (!this.listeners.has(key)) {
            this.listeners.set(key, []);
        }
        this.listeners.get(key)!.push(handler);
    }

    public off<K extends keyof HCIEEventMap>(event: K, handler: EventHandler<K>): void {
        const key = event as string;
        if (!this.listeners.has(key)) return;
        
        const handlers = this.listeners.get(key)!;
        const index = handlers.indexOf(handler);
        if (index > -1) {
            handlers.splice(index, 1);
        }
    }

    public emit<K extends keyof HCIEEventMap>(event: K, payload?: HCIEEventMap[K]): void {
        const key = event as string;
        if (!this.listeners.has(key)) return;
        
        const handlers = this.listeners.get(key)!;
        handlers.forEach(handler => {
            try {
                handler(payload);
            } catch (e) {
                console.error(`Error in event handler for ${key}:`, e);
            }
        });
    }
}

export const EventBus = new EventBusImpl();
